import React, { useState, useEffect, useContext } from 'react';
import { Card, Table, Badge, Button, Form, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiFilter, FiPlus, FiArrowRight, FiArrowLeft, FiClock, FiMap } from 'react-icons/fi';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';

function Rutas() {
    const [routes, setRoutes] = useState([]);
    const [filteredRoutes, setFilteredRoutes] = useState([]);
    const [direction, setDirection] = useState('');
    const [searchString, setSearchString] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const context = useContext(AppContext);
    const navigate = useNavigate();

    // Load routes
    const loadRoutes = () => {
        setIsLoading(true);
        tre.api('GET', '/companies/' + context.user.company_id + '/routes', {
            context: context,
            success: (r) => {
                setRoutes(r);
                setIsLoading(false);
            },
            error: () => setIsLoading(false)
        });
    };

    useEffect(() => {
        loadRoutes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filter routes when filters change
    useEffect(() => {
        let filtered = routes;

        // Filter by direction
        if (direction) {
            filtered = filtered.filter(r => r.direction === direction);
        }

        // Filter by search string
        if (searchString) {
            const search = searchString.toLowerCase();
            filtered = filtered.filter(r =>
                (r.name && r.name.toLowerCase().includes(search)) ||
                tre.wdName(r.weekday).toLowerCase().includes(search)
            );
        }

        setFilteredRoutes(filtered);
    }, [routes, direction, searchString]);

    const onSelect = (route) => {
        navigate('/route/' + route.id);
    };

    // -----| Render |-----
    return (
        <div className="container py-4">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h4 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>
                        <FiMap className="me-2" style={{ color: '#1a73e8' }} />
                        Rutas
                    </h4>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {filteredRoutes.length} rutas en total
                    </p>
                </div>
                <Button
                    variant="primary"
                    onClick={() => navigate('/route/create')}
                >
                    <FiPlus className="me-2" />
                    Nueva Ruta
                </Button>
            </div>

            {/* Filters - Inline style */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                {/* Search */}
                <div className="position-relative" style={{ minWidth: '280px', flex: 1, maxWidth: '400px' }}>
                    <FiSearch
                        className="position-absolute"
                        style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#5f6368' }}
                    />
                    <Form.Control
                        type="text"
                        placeholder="Buscar por nombre o día..."
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                        style={{ paddingLeft: '38px', backgroundColor: 'white' }}
                    />
                </div>

                {/* Direction filter pills */}
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted me-1" style={{ fontSize: '14px' }}>Dirección:</span>
                    <Button
                        size="sm"
                        variant={direction === '' ? 'primary' : 'light'}
                        onClick={() => setDirection('')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        Todas
                    </Button>
                    <Button
                        size="sm"
                        variant={direction === 'GO' ? 'info' : 'light'}
                        onClick={() => setDirection('GO')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        <FiArrowRight className="me-1" /> Ida
                    </Button>
                    <Button
                        size="sm"
                        variant={direction === 'COME' ? 'success' : 'light'}
                        onClick={() => setDirection('COME')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        <FiArrowLeft className="me-1" /> Vuelta
                    </Button>
                </div>

                {/* Clear filters - only show if filters active */}
                {(searchString || direction) && (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => { setDirection(''); setSearchString(''); }}
                        style={{ color: '#ea4335', textDecoration: 'none' }}
                    >
                        <FiFilter className="me-1" /> Limpiar
                    </Button>
                )}
            </div>

            {/* Table Card */}
            <Card>
                {/* Top Info */}
                {!isLoading && filteredRoutes.length > 0 && (
                    <Card.Header className="d-flex justify-content-between align-items-center py-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <span style={{ fontSize: '14px', color: '#5f6368' }}>
                            <strong style={{ color: '#202124' }}>{filteredRoutes.length}</strong> rutas
                        </span>
                    </Card.Header>
                )}

                <Card.Body className="p-0">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" style={{ color: '#1a73e8' }} />
                            <p className="mt-3 text-muted">Cargando rutas...</p>
                        </div>
                    ) : filteredRoutes.length > 0 ? (
                        <Table hover responsive className="mb-0">
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th className="py-3 px-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>DIRECCIÓN</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>NOMBRE</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>DÍA</th>
                                    <th className="py-3 px-3 text-end" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>SALIDA</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRoutes.map((route) => (
                                    <tr
                                        key={route.id}
                                        onClick={() => onSelect(route)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td className="py-3 px-3">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                                    style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        backgroundColor: route.direction === 'GO' ? '#e8f0fe' : '#e6f4ea',
                                                        color: route.direction === 'GO' ? '#1a73e8' : '#34a853'
                                                    }}
                                                >
                                                    {route.direction === 'GO' ? (
                                                        <FiArrowRight size={16} />
                                                    ) : (
                                                        <FiArrowLeft size={16} />
                                                    )}
                                                </div>
                                                <Badge
                                                    bg={route.direction === 'GO' ? 'info' : 'success'}
                                                    style={{ fontSize: '12px', padding: '6px 12px', fontWeight: 500 }}
                                                >
                                                    {route.direction === 'GO' ? 'IDA' : 'VUELTA'}
                                                </Badge>
                                            </div>
                                        </td>
                                        <td className="py-3">
                                            <span style={{ color: '#202124', fontWeight: 500 }}>
                                                {route.name || '-'}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <span className="text-muted" style={{ fontSize: '14px' }}>
                                                {tre.wdName(route.weekday)}
                                            </span>
                                        </td>
                                        <td className="py-3 px-3 text-end">
                                            <span className="d-inline-flex align-items-center" style={{ color: '#5f6368' }}>
                                                <FiClock className="me-1" />
                                                {route.departure && moment(route.departure, 'HH:mm:ss').format('H:mm')}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center py-5">
                            <FiMap size={48} className="mb-3" style={{ color: '#dadce0' }} />
                            <p className="text-muted">No se encontraron rutas</p>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}

export default Rutas;
